/**
 * Price Database Operations
 * 
 * Client-side database operations for prices.
 * Prices are shared resources (no user scoping needed).
 */

import { collection, query, where, getDocs, onSnapshot, QuerySnapshot, Timestamp } from 'firebase/firestore';
import { getDb } from '@/lib/firebase-config';
import { Price } from '../types/price';

const PRICES_COLLECTION = 'prices';

/**
 * Get all prices for a product
 */
export async function getPricesByProductId(productId: string): Promise<Price[]> {
  try {
    const db = getDb();
    const pricesRef = collection(db, PRICES_COLLECTION);
    const q = query(
      pricesRef,
      where('productId', '==', productId),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Price[];
  } catch (error) {
    logger.error('Error fetching prices by product ID:', error);
    throw error;
  }
}
import logger from '@/lib/logging';

/**
 * Subscribe to prices for a product with real-time updates
 */
export function subscribeToPricesByProductId(
  productId: string,
  onUpdate: (prices: Price[]) => void,
  onError: (error: Error) => void
): () => void {
  try {
    const db = getDb();
    const pricesRef = collection(db, PRICES_COLLECTION);
    const q = query(
      pricesRef,
      where('productId', '==', productId),
      where('isActive', '==', true)
    );

    return onSnapshot(
      q,
      (snapshot: QuerySnapshot) => {
        try {
          const prices = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Price[];
          onUpdate(prices);
        } catch (error) {
          logger.error('Error processing price snapshot:', error);
          onError(error instanceof Error ? error : new Error(String(error)));
        }
      },
      (error) => {
        logger.error('Error subscribing to prices:', error);
        onError(error);
      }
    );
  } catch (error) {
    logger.error('Error setting up price subscription:', error);
    onError(error instanceof Error ? error : new Error(String(error)));
    // Return a no-op unsubscribe function
    return () => {};
  }
}

/**
 * Subscribe to all prices, active and inactive (admin Products table)
 */
export function subscribeToAllPrices(
  onUpdate: (prices: Price[]) => void,
  onError: (error: Error) => void
): () => void {
  try {
    const db = getDb();
    return onSnapshot(
      collection(db, PRICES_COLLECTION),
      (snapshot: QuerySnapshot) => {
        onUpdate(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Price[]
        );
      },
      (error) => {
        logger.error('Error subscribing to all prices:', error);
        onError(error);
      }
    );
  } catch (error) {
    logger.error('Error setting up all-prices subscription:', error);
    onError(error instanceof Error ? error : new Error(String(error)));
    return () => {};
  }
}

/**
 * Get price by Stripe price ID
 */
export async function getPriceByStripeId(providerPriceId: string): Promise<Price | null> {
  try {
    const db = getDb();
    const pricesRef = collection(db, PRICES_COLLECTION);
    const q = query(pricesRef, where('providerPriceId', '==', providerPriceId));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    return {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data(),
    } as Price;
  } catch (error) {
    logger.error('Error fetching price by Stripe ID:', error);
    throw error;
  }
}

